import { FileText } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { DeliveryNote } from "@/types";

import EvidenceAnalysisSection from "./evidence-analysis-section";

type DeliveryNoteFile = NonNullable<DeliveryNote["processed_pdf"]>;

interface AttachmentsSectionProps {
  processedPdf: DeliveryNote["processed_pdf"];
  remitFiles: DeliveryNote["remit_files"];
  evidenceFiles: DeliveryNote["evidence_files"];
  analysisStatus?: string;
  analysisResults?: DeliveryNote["evidence_analysis_results"];
  onRefresh?: () => void;
}

function AttachmentLink({ file }: { file: DeliveryNoteFile }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      asChild
      className="h-auto justify-start px-3 py-2"
    >
      <a href={file.url} target="_blank" rel="noopener noreferrer">
        <FileText className="h-4 w-4 mr-2" />
        <span className="text-sm">{file.filename}</span>
      </a>
    </Button>
  );
}

export default function AttachmentsSection({
  processedPdf,
  remitFiles,
  evidenceFiles,
  analysisStatus,
  analysisResults,
  onRefresh,
}: AttachmentsSectionProps) {
  if (!processedPdf && remitFiles.length === 0 && evidenceFiles.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-base font-medium">Archivos Adjuntos</h2>
      <div className="space-y-4">
        {processedPdf && (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              PDF Procesado
            </p>
            <AttachmentLink file={processedPdf} />
          </div>
        )}

        {remitFiles.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Remitos
            </p>
            <div className="flex flex-col gap-1">
              {remitFiles.map((file) => (
                <AttachmentLink key={file.url} file={file} />
              ))}
            </div>
          </div>
        )}

        {evidenceFiles.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Evidencias
            </p>
            <div className="flex flex-col gap-1">
              {evidenceFiles.map((file) => (
                <AttachmentLink key={file.url} file={file} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Evidence Analysis Section */}
      {evidenceFiles.length > 0 && (
        <>
          <div className="pt-4">
            <EvidenceAnalysisSection
              evidenceFiles={evidenceFiles}
              analysisStatus={analysisStatus}
              analysisResults={analysisResults}
              onRefresh={onRefresh}
            />
          </div>
        </>
      )}
    </div>
  );
}
